import { HttpInterceptorFn } from '@angular/common/http';
import { injectAppVersion } from '@seatix/shared-util-app-version';
import {
  injectVersionHeaderAllowedUrls,
  injectVersionHeaderName,
  isUrlAllowed,
} from './version-providers';

/**
 * Functional HTTP interceptor that adds application version information
 * to filtered outgoing API requests via a configurable version header.
 *
 * This enables backend teams to identify which application version
 * made specific API requests for debugging and monitoring purposes.
 *
 * @example
 * ```typescript
 * // In app.config.ts
 * export const appConfig: ApplicationConfig = {
 *   providers: [
 *     // ... other providers
 *     provideHttpClient(withInterceptors([versionInterceptor])),
 *     provideVersionInterceptorConfig({
 *       headerName: 'X-App-Version',
 *       allowedUrls: ['https://api.example.com']
 *     }),
 *   ],
 * };
 * ```
 */
export const versionInterceptor: HttpInterceptorFn = (req, next) => {
  const appVersion = injectAppVersion();
  const headerName = injectVersionHeaderName();
  const allowedUrls = injectVersionHeaderAllowedUrls();

  if (!appVersion || !headerName) {
    return next(req);
  }

  if (!isUrlAllowed(req.url, allowedUrls)) {
    return next(req);
  }

  const versionedRequest = req.clone({
    setHeaders: {
      [headerName]: appVersion,
    },
  });

  return next(versionedRequest);
};
